import React, { useState, useEffect } from 'react';
import { AlertTriangle, Plus, Edit2, Trash2, Check, AlertCircle, Info, XCircle, Calendar } from 'lucide-react';
import systemAlertService from '../../services/systemAlertService';
import ConfirmDialog from '../common/ConfirmDialog';

const SystemAlertManager = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAlert, setEditingAlert] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  // Form state for creating/editing
  const [formData, setFormData] = useState({
    message: '',
    severity: 'info',
    is_enabled: true,
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await systemAlertService.getAllAlerts();
      setAlerts(data);
    } catch (err) {
      console.error('Error loading alerts:', err);
      setError(err.response?.data?.detail || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      message: '',
      severity: 'info',
      is_enabled: true,
      start_date: '',
      end_date: ''
    });
  };

  const handleCreate = () => {
    resetForm();
    setEditingAlert(null);
    setIsCreating(true);
  };

  const handleEdit = (alert) => {
    setFormData({
      message: alert.message,
      severity: alert.severity,
      is_enabled: alert.is_enabled,
      start_date: alert.start_date ? alert.start_date.slice(0, 16) : '',
      end_date: alert.end_date ? alert.end_date.slice(0, 16) : ''
    });
    setEditingAlert(alert);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setEditingAlert(null);
    setIsCreating(false);
    resetForm();
  };

  const handleSave = async () => {
    try {
      setSavingId(editingAlert?.id || 'new');
      setError(null);

      const payload = {
        message: formData.message,
        severity: formData.severity,
        is_enabled: formData.is_enabled,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      if (isCreating) {
        const newAlert = await systemAlertService.createAlert(payload);
        setAlerts(prev => [newAlert, ...prev]);
      } else {
        const updatedAlert = await systemAlertService.updateAlert(editingAlert.id, payload);
        setAlerts(prev => prev.map(a => a.id === editingAlert.id ? updatedAlert : a));
      }

      handleCancel();
    } catch (err) {
      console.error('Error saving alert:', err);
      setError(err.response?.data?.detail || 'Failed to save alert');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = (alertId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Alert',
      message: 'Are you sure you want to delete this alert?',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          setSavingId(alertId);
          await systemAlertService.deleteAlert(alertId);
          setAlerts(prev => prev.filter(a => a.id !== alertId));
        } catch (err) {
          console.error('Error deleting alert:', err);
          setError(err.response?.data?.detail || 'Failed to delete alert');
        } finally {
          setSavingId(null);
        }
      },
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const severityConfig = {
    info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-100' },
    warning: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-100' },
    error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-3 text-gray-600">Loading alerts...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">System Alerts</h2>
              <p className="text-sm text-gray-500 mt-1">
                Create and manage system-wide alert banners (Super Admin only)
              </p>
            </div>
          </div>
          {!isCreating && !editingAlert && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Alert
            </button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingAlert) && (
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isCreating ? 'Create New Alert' : 'Edit Alert'}
          </h3>
          <div className="space-y-4">
            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Enter the alert message..."
              />
            </div>

            {/* Severity and Enabled */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity
                </label>
                <select
                  value={formData.severity}
                  onChange={(e) => handleInputChange('severity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="info">Info (Blue)</option>
                  <option value="warning">Warning (Yellow)</option>
                  <option value="error">Error (Red)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.is_enabled ? 'enabled' : 'disabled'}
                  onChange={(e) => handleInputChange('is_enabled', e.target.value === 'enabled')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Start Date (optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to show immediately</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  End Date (optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to show indefinitely</p>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                onClick={handleCancel}
                disabled={savingId}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={savingId || !formData.message.trim()}
                className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingId ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {isCreating ? 'Create Alert' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alerts List */}
      <div className="divide-y divide-gray-200">
        {alerts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <AlertTriangle className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p>No system alerts configured</p>
            <p className="text-sm mt-1">Create an alert to notify all users in your firm</p>
          </div>
        ) : (
          alerts.map((alert) => {
            const config = severityConfig[alert.severity] || severityConfig.info;
            const SeverityIcon = config.icon;
            const isSaving = savingId === alert.id;
            const isCurrentlyEditing = editingAlert?.id === alert.id;

            return (
              <div
                key={alert.id}
                className={`p-4 ${isCurrentlyEditing ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <div className={`p-2 rounded-full ${config.bg} mr-3`}>
                      <SeverityIcon className={`h-5 w-5 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{alert.message}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            alert.is_enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {alert.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.color}`}>
                          {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                        </span>
                        {alert.start_date && (
                          <span className="text-xs text-gray-500">
                            Starts: {new Date(alert.start_date).toLocaleString()}
                          </span>
                        )}
                        {alert.end_date && (
                          <span className="text-xs text-gray-500">
                            Ends: {new Date(alert.end_date).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(alert)}
                      disabled={isSaving || isCreating}
                      className="p-1.5 text-gray-400 hover:text-yellow-600 disabled:opacity-50"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(alert.id)}
                      disabled={isSaving || isCreating}
                      className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      title="Delete"
                    >
                      {isSaving ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  );
};

export default SystemAlertManager;
